/**
 * Command Palette — keyboard-first overlay (Ctrl+K / Cmd+K).
 *
 * Provides quick navigation to Fleet, Work, KB views, and active runs.
 * Pattern: command-palette-e2c41b
 */

import { esc } from './shared.js'

export interface Command {
  id: string
  label: string
  description?: string
  shortcut?: string
  icon?: string
  action: () => void
}

let _isOpen = false
let _backdropAttached = false
let _selectedIndex = 0
let _commands: Command[] = []
let _filtered: Command[] = []

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Register commands and attach Ctrl+K listener to the document. */
export function initCommandPalette(commands: Command[]): void {
  _commands = commands
  document.addEventListener('keydown', handleKeyDown)
}

/** Open the palette (for manual invocation). */
export function openCommandPalette(): void {
  if (_isOpen) {
    return
  }
  _isOpen = true
  _selectedIndex = 0
  _filtered = [..._commands]
  renderPalette()
  if (!_backdropAttached) {
    attachBackdrop()
    _backdropAttached = true
  }
}

/** Close the palette. */
export function closeCommandPalette(): void {
  if (!_isOpen) {
    return
  }
  _isOpen = false
  _backdropAttached = false
  const overlay = document.getElementById('cmd-palette-overlay')
  overlay?.remove()
}

// ---------------------------------------------------------------------------
// Keyboard handling
// ---------------------------------------------------------------------------

function handleKeyDown(e: KeyboardEvent): void {
  const isMac = (
    (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    ''
  )
    .toUpperCase()
    .includes('MAC')
  const modifier = isMac ? e.metaKey : e.ctrlKey

  if (modifier && e.key === 'k') {
    e.preventDefault()
    if (_isOpen) {
      closeCommandPalette()
    } else {
      openCommandPalette()
    }
    return
  }

  if (!_isOpen) {
    return
  }

  if (e.key === 'Escape') {
    e.preventDefault()
    closeCommandPalette()
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    _selectedIndex = Math.min(_selectedIndex + 1, _filtered.length - 1)
    updateSelection()
    return
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault()
    _selectedIndex = Math.max(_selectedIndex - 1, 0)
    updateSelection()
    return
  }

  if (e.key === 'Enter') {
    e.preventDefault()
    const cmd = _filtered[_selectedIndex]
    if (cmd) {
      closeCommandPalette()
      cmd.action()
    }
    return
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderPalette(): void {
  const existing = document.getElementById('cmd-palette-overlay')
  existing?.remove()

  const isMac = (
    (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    ''
  )
    .toUpperCase()
    .includes('MAC')
  const shortcutLabel = isMac ? '⌘K' : 'Ctrl+K'

  const html = `
  <div id="cmd-palette-overlay" class="cmd-palette-overlay active" role="dialog"
       aria-modal="true" aria-label="Command palette">
    <div class="cmd-palette" onclick="event.stopPropagation()">
      <div class="cmd-palette-input-wrap">
        <span class="cmd-palette-icon" aria-hidden="true">⌕</span>
        <input type="text" class="cmd-palette-input" id="cmd-palette-input"
               placeholder="Search commands and runs..."
               aria-label="Search commands" aria-autocomplete="list"
               autocomplete="off" />
        <kbd class="cmd-palette-esc-hint">ESC</kbd>
      </div>
      <div class="cmd-palette-results" id="cmd-palette-results" role="listbox">
        ${renderResults()}
      </div>
      <div class="cmd-palette-footer">
        <span class="cmd-palette-footer-hint">
          <kbd>↑↓</kbd> navigate &nbsp; <kbd>↵</kbd> select &nbsp; <kbd>ESC</kbd> close
        </span>
        <span class="cmd-palette-shortcut-tip">${esc(shortcutLabel)}</span>
      </div>
    </div>
  </div>`

  document.body.insertAdjacentHTML('beforeend', html) /* nosemgrep */

  const input = document.getElementById('cmd-palette-input') as HTMLInputElement
  input?.focus()

  input?.addEventListener('input', () => {
    _filtered = filterCommands(input.value)
    _selectedIndex = 0
    const resultsEl = document.getElementById('cmd-palette-results')
    if (resultsEl) {
      resultsEl.innerHTML = renderResults()
    }
    updateSelection()
  })
}

function renderResults(): string {
  if (_filtered.length === 0) {
    return `<div class="cmd-palette-empty">No matching commands.</div>`
  }
  return _filtered
    .map(
      (cmd, i) => `
    <div class="cmd-palette-item${i === _selectedIndex ? ' selected' : ''}"
         data-index="${i}" role="option"
         aria-selected="${i === _selectedIndex}"
         tabindex="0"
         onclick="__cmdSelect(${String(i)})"
         onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();__cmdSelect(${String(i)})}">
      <div class="cmd-palette-item-icon" aria-hidden="true">${cmd.icon ?? '\u25B6'}</div>
      <div class="cmd-palette-item-content">
        <div class="cmd-palette-item-title">${esc(cmd.label)}</div>
        ${cmd.description ? `<div class="cmd-palette-item-desc">${esc(cmd.description)}</div>` : ''}
      </div>
      ${cmd.shortcut ? `<kbd class="cmd-palette-item-kbd">${esc(cmd.shortcut)}</kbd>` : ''}
    </div>`,
    )
    .join('')
}

function updateSelection(): void {
  document.querySelectorAll('.cmd-palette-item').forEach((el, i) => {
    const selected = i === _selectedIndex
    el.classList.toggle('selected', selected)
    el.setAttribute('aria-selected', String(selected))
  })
  // Scroll selected item into view
  const selected = document.querySelector('.cmd-palette-item.selected')
  selected?.scrollIntoView({ block: 'nearest' })
}

export function filterCommands(query: string): Command[] {
  if (!query.trim()) {
    return [..._commands]
  }
  const q = query.toLowerCase()
  return _commands.filter(
    (c) => c.label.toLowerCase().includes(q) || (c.description?.toLowerCase().includes(q) ?? false),
  )
}

// ---------------------------------------------------------------------------
// Backdrop and interaction
// ---------------------------------------------------------------------------

function attachBackdrop(): void {
  const overlay = document.getElementById('cmd-palette-overlay')
  if (!overlay) {
    return
  }
  overlay.addEventListener('click', closeCommandPalette)

  // Prevent clicks inside the palette from closing
  overlay.querySelector('.cmd-palette')?.addEventListener('click', (e) => {
    e.stopPropagation()
  })
}

// ---------------------------------------------------------------------------
// Global selection handler (set by onclick in rendered HTML)
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    __cmdSelect?: (index: number) => void
  }
}

window.__cmdSelect = (index: number): void => {
  const cmd = _filtered[index]
  if (cmd) {
    closeCommandPalette()
    cmd.action()
  }
}
